import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Typography, Tabs, Tab, Box } from "@mui/material";
import AllLocationsTable from "./AllLocationsTable";

const AllLocationsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [buttonColor, setButtonColor] = useState("#000000");
  const [menuUrl, setMenuUrl] = useState("");
  const [wayfinderEnabled, setWayfinderEnabled] = useState(false);
  
  // Language support
  const [availableLanguages, setAvailableLanguages] = useState(['en']);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  // Fetch user data for menu URL and brand color
  const fetchUserData = async () => {
    try {
      const res = await axios.get(`/auth/aiform/all/${userId}`);
      const data = res.data?.UserData?.menu_client_url;
      const brandColor = res.data?.UserData?.brandColor;
      if (data) {
        setMenuUrl(data);
        setButtonColor(brandColor);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch client settings to check feature access
  const fetchClientSettings = async () => {
    try {
      const res = await axios.get(`/auth/settings/client/${userId}`);
      const featureAccess = res.data?.settings?.featureAccess;
      setWayfinderEnabled(featureAccess?.wayfinderEnabled || false);
    } catch (error) {
      console.error("Error fetching client settings:", error);
      setWayfinderEnabled(false);
    }
  };

  // Fetch available languages
  const fetchAvailableLanguages = async () => {
    try {
      setLoadingLanguages(true);
      const response = await axios.get(`/api/language/enabled/${userId}`);
      if (response.data.success && response.data.languages) {
        const enabledLangCodes = response.data.languages;
        setAvailableLanguages(enabledLangCodes.length > 0 ? enabledLangCodes : ['en']);

        const savedLang = localStorage.getItem(`language_pref_${userId}`);
        if (savedLang && enabledLangCodes.includes(savedLang)) {
          setCurrentLanguage(savedLang);
        } else {
          const defaultLang = enabledLangCodes.includes('en') ? 'en' : (enabledLangCodes[0] || 'en');
          setCurrentLanguage(defaultLang);
        }
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      setAvailableLanguages(['en']);
      setCurrentLanguage('en');
    } finally {
      setLoadingLanguages(false);
    }
  };

  // Function to change language
  const handleLanguageChange = async (langCode) => {
    setCurrentLanguage(langCode);
    localStorage.setItem(`language_pref_${userId}`, langCode);
  };

  // Helper function to get language name
  const getLanguageName = (code) => {
    if (!code) return 'Unknown';

    const languageNames = {
      en: 'English',
      es: 'Spanish',
      fr: 'French',
      de: 'German',
      it: 'Italian',
      pt: 'Portuguese',
      ru: 'Russian',
      zh: 'Chinese',
      ja: 'Japanese',
      ko: 'Korean',
      ar: 'Arabic',
      hi: 'Hindi',
      tr: 'Turkish',
      nl: 'Dutch',
      sv: 'Swedish',
      pl: 'Polish',
      th: 'Thai',
      vi: 'Vietnamese',
      id: 'Indonesian',
    };
    return languageNames[code] || code.toUpperCase();
  };

  // Helper function to get language flag
  const getLanguageFlag = (code) => {
    if (!code) return 'xx';

    const flagMap = {
      en: 'us',
      es: 'es',
      fr: 'fr',
      de: 'de',
      it: 'it',
      pt: 'pt',
      ru: 'ru',
      zh: 'cn',
      ja: 'jp',
      ko: 'kr',
      ar: 'sa',
      hi: 'in',
      tr: 'tr',
      nl: 'nl',
      sv: 'se',
      pl: 'pl',
      th: 'th',
      vi: 'vn',
      id: 'id',
    };
    return flagMap[code] || 'xx';
  };

  const goBack = () => {
    navigate(-1);
  };

  // Handle tab changes - navigate to appropriate routes
  const handleTabChange = (_event, newValue) => {
    // Tab route mapping
    const getTabRoute = (index) => {
      const routes = wayfinderEnabled 
        ? [
            `/view-button-settings/${userId}`, // Main Menu
            `/wayfinder/categories/${userId}`,  // Category's
            `/wayfinder/locations/${userId}`,   // Locations
            `/wayfinder/all-locations/${userId}`, // All Locations
            `/wayfinder/branding/${userId}`,    // Branding & QR
            `/wayfinder/mobile/${userId}`,      // Mobile
            `/wayfinder/global/${userId}`,      // Global
            `/wayfinder/avatar/${userId}`,      // Avatar API
            `/wayfinder/map/${userId}`,         // MAP
            `/wayfinder/captions/${userId}`,    // Captions
            `/wayfinder/videos/${userId}`,      // Videos
            `/wayfinder/tags/${userId}`,        // TAGS
          ]
        : [
            `/wayfinder/categories/${userId}`,  // Category's
            `/wayfinder/locations/${userId}`,   // Locations
            `/wayfinder/all-locations/${userId}`, // All Locations
            `/wayfinder/branding/${userId}`,    // Branding & QR
            `/wayfinder/mobile/${userId}`,      // Mobile
            `/wayfinder/global/${userId}`,      // Global
            `/wayfinder/avatar/${userId}`,      // Avatar API
            `/wayfinder/map/${userId}`,         // MAP
            `/wayfinder/captions/${userId}`,    // Captions
            `/wayfinder/videos/${userId}`,      // Videos
            `/wayfinder/tags/${userId}`,        // TAGS
          ];
      
      return routes[index];
    };

    const route = getTabRoute(newValue);
    if (route) {
      navigate(route);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserData();
      fetchClientSettings();
      fetchAvailableLanguages();
    }
  }, [userId]);

  return (
    <div className="main-container">
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px dashboard-container">
          {/* Header Section */}
          <div className="page-header" style={{ marginBottom: "24px" }}>
            <div className="row">
              <div className="col-md-3 col-sm-12">
                <div className="title">
                  <Typography variant="h4">
                    Wayfinder - All Locations
                    {menuUrl && (
                      <>
                        <Typography
                          component="span"
                          sx={{ fontWeight: "bold", mx: 1 }}
                        >
                          &gt;&gt;
                        </Typography>
                        {menuUrl.charAt(0).toUpperCase() + menuUrl.slice(1)}
                      </>
                    )}
                  </Typography>
                </div>
              </div>

              {/* Language Switcher */}
              <div className="col-md-3 col-sm-12 d-flex align-items-center justify-content-center">
                {loadingLanguages ? (
                  <div className="d-flex align-items-center">
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span>Loading languages...</span>
                  </div>
                ) : availableLanguages.length > 1 ? (
                  <div className="language-switcher">
                    <div className="dropdown">
                      <button
                        className="btn btn-outline-secondary dropdown-toggle d-flex align-items-center"
                        type="button"
                        id="languageDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style={{
                          borderColor: buttonColor,
                          color: buttonColor,
                          backgroundColor: "transparent",
                        }}
                      >
                        <span
                          className={`fi fi-${getLanguageFlag(currentLanguage)} me-2`}
                          style={{ fontSize: "16px" }}
                        ></span>
                        <span className="me-1">
                          {getLanguageName(currentLanguage)}
                        </span>
                      </button>
                      <ul className="dropdown-menu" aria-labelledby="languageDropdown">
                        {availableLanguages.map((lang) => (
                          <li key={lang}>
                            <button
                              className={`dropdown-item d-flex align-items-center ${lang === currentLanguage ? "active" : ""
                                }`}
                              onClick={() => handleLanguageChange(lang)}
                              style={{
                                backgroundColor:
                                  lang === currentLanguage ? buttonColor : "transparent",
                                color: lang === currentLanguage ? "#fff" : "#000",
                              }}
                            >
                              <span
                                className={`fi fi-${getLanguageFlag(lang)} me-2`}
                                style={{ fontSize: "16px" }}
                              ></span>
                              {getLanguageName(lang)}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="col-md-6 col-sm-12">
                <button
                  type="button"
                  onClick={goBack}
                  style={{ float: "right", backgroundColor: buttonColor }}
                  className="btn btn-success"
                >
                  {`<< Back`}
                </button>
              </div>
            </div>
          </div>

          {/* Main Tabs */}
          <Tabs
            value={wayfinderEnabled ? 3 : 2}
            onChange={handleTabChange}
            aria-label="wayfinder tabs"
            sx={{
              "& .MuiTab-root": { minWidth: 100 },
              "& .Mui-selected": { fontWeight: "bold" },
            }}
          >
            {wayfinderEnabled && (
              <Tab
                sx={{
                  "&:hover": {
                    backgroundColor: buttonColor,
                    color: "#fff",
                    fontWeight: "bold",
                  },
                }}
                label="Main Menu"
              />
            )}

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Category's"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Locations"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="All Locations"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Branding & QR"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Mobile"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Global"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Avatar API"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="MAP"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Captions"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Videos"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="TAGS"
            />
          </Tabs>

          {/* All Locations Table */}
          <AllLocationsTable buttonColor={buttonColor} />
        </div>
      </div>
    </div>
  );
};

export default AllLocationsPage;
